import { MentionedAsset } from "../components/AiChatComponent/hooks/useAtMention";

// Marker delimiting the @-mention asset-context block appended to a prompt. Kept as a literal so
// stripAssetContext can reliably find and remove the block when editing a saved schedule prompt.
const ASSET_CONTEXT_PREFIX = "\n\n[Asset context:\n";

// Cap the member-name list on a group line so a huge group can't blow up the prompt.
const GROUP_MEMBER_NAME_LIMIT = 15;

// Build the "[Asset context: …]" block appended to a prompt for the model. Extracted from
// AiAnalystPage so chat and the schedule modals share one implementation.
// `dataGroups` (the assets-by-group dict from state.assets.dataGroups) supplies member
// names for group mentions; asset lines are unchanged with or without it.
export function buildAssetContext(
  assets: MentionedAsset[],
  dataGroups?: Record<string, { name: string }[]>
): string {
  if (assets.length === 0) return "";
  const lines = assets.map((a) => {
    if (a.kind === "group") {
      const members = dataGroups?.[a.id] ?? [];
      const parts = [`Group: ${a.name} (id: ${a.id})`];
      if (a.description) parts.push(a.description);
      if (members.length > 0) {
        const names = members.slice(0, GROUP_MEMBER_NAME_LIMIT).map((m) => m.name);
        const overflow = members.length - names.length;
        parts.push(
          `${members.length} assets: ${names.join(", ")}${overflow > 0 ? `, +${overflow} more` : ""}`
        );
      }
      return `- ${parts.join(" — ")}`;
    }
    const parts = [`${a.name} (id: ${a.id})`];
    if (a.description) parts.push(a.description);
    if (a.lat != null && a.lng != null) parts.push(`coordinates: ${a.lat}, ${a.lng}`);
    return `- ${parts.join(" — ")}`;
  });
  return `${ASSET_CONTEXT_PREFIX}${lines.join("\n")}]`;
}

// Remove a previously-baked "[Asset context: …]" block so the editor shows the clean prompt
// (plus the @-mention tokens, from which chips are re-derived). Schedules bake the context into
// the stored prompt rather than a separate field, so editing must strip then re-bake.
export function stripAssetContext(prompt: string): string {
  const idx = prompt.indexOf(ASSET_CONTEXT_PREFIX);
  return idx === -1 ? prompt : prompt.slice(0, idx);
}
